// iter 144d - Public partner application page.
//
// Route: /become-a-partner
// Public, no auth. Two GA4 events:
//   partner_application_started   - on first render
//   partner_application_submitted - on successful form submission
//
// File uploads go to the backend (multipart) - backend uploads to
// Cloudinary so we never expose unsigned upload presets to the public
// for sensitive credential documents.

import React, { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { trackEvent } from '../lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL || '';

const SERVICE_TYPES = [
  'Immigration Attorney',
  'Real Estate Attorney',
  'General Practice Attorney',
  'Healthcare Provider - Doctor or Specialist',
  'Healthcare Provider - Dentist',
  'Health Insurance Broker',
  'International Tax Accountant or CPA',
  'Financial Advisor',
  'Real Estate Agent or Broker',
  'Property Manager',
  'Relocation Consultant',
  'Vehicle Purchase Advisory',
  'Moving and Shipping Services',
  'Language School',
  'Other',
];

const LANGUAGES = ['English', 'Spanish', 'Both', 'Other'];
const EXPAT_OPTIONS = ['Less than 10', '10 to 50', '50 to 100', 'More than 100'];

const MAX_BYTES = 10 * 1024 * 1024; // 10MB
const ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';

const labelCls =
  'font-mono uppercase text-[10px] tracking-[0.18em] text-[#0A1F2E]/70 mb-2 block';
const inputCls =
  'w-full bg-white border border-[#d5d0c8] text-[#0A1F2E] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#2CA8A0] focus:ring-2 focus:ring-[#2CA8A0]/25';

const ValueCard = ({ eyebrow, body, testId }) => (
  <div
    data-testid={testId}
    className="bg-[#FFFFFF] border border-[#0A1F2E]/10 rounded-2xl p-7"
  >
    <p className="font-mono uppercase text-[10px] tracking-[0.20em] text-[#E8A830] mb-3">
      {eyebrow}
    </p>
    <p className="font-body text-[#0A1F2E] text-[15px] leading-relaxed">
      {body}
    </p>
  </div>
);

export default function BecomeAPartner() {
  const [form, setForm] = useState({
    service_type: '',
    applicant_name: '',
    business_name: '',
    cities: '',
    languages: [],
    contact_email: '',
    phone: '',
    website: '',
    description: '',
    fee_structure: '',
    expat_clients_count: '',
    agreement_accuracy: false,
    agreement_platform: false,
  });
  const [credentialFiles, setCredentialFiles] = useState([]);
  const [idFile, setIdFile] = useState(null);
  const [supportingFiles, setSupportingFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    trackEvent('partner_application_started');
  }, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleLanguage = (lang) =>
    setForm((p) => ({
      ...p,
      languages: p.languages.includes(lang)
        ? p.languages.filter((l) => l !== lang)
        : [...p.languages, lang],
    }));

  const validateFile = (f) => {
    if (!f) return null;
    if (f.size > MAX_BYTES) return `${f.name} is over 10 MB`;
    const ok = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'].includes(
      f.type.toLowerCase()
    );
    if (!ok) return `${f.name} must be PDF, JPG, or PNG`;
    return null;
  };

  const onCredentialFiles = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3);
    for (const f of files) {
      const err = validateFile(f);
      if (err) {
        setSubmitError(err);
        e.target.value = '';
        return;
      }
    }
    setSubmitError('');
    setCredentialFiles(files);
  };

  const onIdFile = (e) => {
    const f = (e.target.files || [])[0];
    if (!f) return;
    const err = validateFile(f);
    if (err) {
      setSubmitError(err);
      e.target.value = '';
      return;
    }
    setSubmitError('');
    setIdFile(f);
  };

  const onSupportingFiles = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5);
    for (const f of files) {
      const err = validateFile(f);
      if (err) {
        setSubmitError(err);
        e.target.value = '';
        return;
      }
    }
    setSubmitError('');
    setSupportingFiles(files);
  };

  const descCount = form.description.length;
  const descOk = descCount >= 100;

  // iter 144d-fix: stricter email check than HTML `type="email"` to match
  // backend Pydantic EmailStr. Catches "john@company" (no TLD) before
  // submission so users see an inline hint instead of a generic 422.
  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const emailTrim = form.contact_email.trim();
  const emailOk = emailRe.test(emailTrim);

  const canSubmit =
    !!form.service_type &&
    !!form.applicant_name.trim() &&
    !!form.business_name.trim() &&
    !!form.cities.trim() &&
    form.languages.length > 0 &&
    emailOk &&
    !!form.phone.trim() &&
    descOk &&
    !!form.fee_structure.trim() &&
    !!form.expat_clients_count &&
    !!form.agreement_accuracy &&
    !!form.agreement_platform &&
    credentialFiles.length > 0 &&
    !!idFile &&
    !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const fd = new FormData();
      fd.append('applicant_name', form.applicant_name.trim());
      fd.append('business_name', form.business_name.trim());
      fd.append('service_type', form.service_type);
      fd.append('cities', form.cities.trim());
      fd.append('languages', form.languages.join(','));
      fd.append('contact_email', form.contact_email.trim());
      fd.append('phone', form.phone.trim());
      fd.append('website', form.website.trim());
      fd.append('description', form.description.trim());
      fd.append('fee_structure', form.fee_structure.trim());
      fd.append('expat_clients_count', form.expat_clients_count);
      fd.append('agreement_accuracy', String(form.agreement_accuracy));
      fd.append('agreement_platform', String(form.agreement_platform));
      credentialFiles.forEach((f) => fd.append('credential_files', f));
      fd.append('id_file', idFile);
      supportingFiles.forEach((f) => fd.append('supporting_files', f));

      const r = await fetch(`${API}/api/partner-applications/submit`, {
        method: 'POST',
        body: fd,
      });
      if (!r.ok) {
        // iter 144d-fix: friendly error mapping. FastAPI returns
        // `detail` as an ARRAY of Pydantic error objects on 422 -
        // stringifying that array shows "[object Object]" to the user.
        // Map every error class to a human-readable sentence instead.
        let userMessage;
        if (r.status === 422) {
          userMessage =
            'Please check your form - some required fields are missing or incomplete.';
        } else if (r.status === 413) {
          userMessage =
            'There was an issue uploading your documents. Please check your file sizes are under 10 MB and try again.';
        } else if (r.status === 502) {
          // Backend wraps Cloudinary failures as 502 in _upload_one.
          userMessage =
            'There was an issue uploading your documents. Please check your file sizes are under 10 MB and try again.';
        } else if (r.status >= 500) {
          userMessage =
            'Something went wrong on our end. Please try again or email us at hello@mundalo.com.';
        } else {
          // 400 / 409 / other 4xx - backend returns `detail` as a STRING
          // for these. Use it if it's a string, otherwise fall back to the
          // generic copy. Never render raw objects.
          let backendDetail = null;
          try {
            const body = await r.json();
            if (typeof body?.detail === 'string') backendDetail = body.detail;
          } catch (_) { /* ignore parse failures */ }
          userMessage =
            backendDetail ||
            'Something went wrong. Please try again or email us at hello@mundalo.com.';
        }
        throw new Error(userMessage);
      }
      trackEvent('partner_application_submitted', {
        service_type: form.service_type,
        cities: form.cities.trim(),
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e2) {
      // e2.message is now ALWAYS a user-friendly string thanks to the
      // mapping above. Any unexpected throw (network blip, JSON parse
      // failure, etc.) gets the generic fallback so we never render
      // "object Object" to the user.
      const msg =
        (e2 && typeof e2.message === 'string' && e2.message) ||
        'Something went wrong. Please try again or email us at hello@mundalo.com.';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Become a Vetted Mundalo Partner | Mundalo</title>
        <meta
          name="description"
          content="List your services to thousands of Americans actively planning their move to Mexico. Apply to join the Mundalo vetted partner network. Free while in early access."
        />
        <link rel="canonical" href="https://mundalo.com/become-a-partner" />
      </Helmet>
      <Header />

      <main className="bg-[#F9F6EF] min-h-screen">
        {/* HERO */}
        <section className="max-w-5xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12">
          <p
            data-testid="bap-hero-eyebrow"
            className="font-mono uppercase text-[11px] tracking-[0.22em] text-[#2CA8A0] mb-5"
          >
            Mundalo Partner Network
          </p>
          <h1
            data-testid="bap-hero-headline"
            className="font-display italic font-medium text-[#0A1F2E] text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-6"
          >
            Reach thousands of Americans moving to Mexico.
          </h1>
          <p className="font-body text-[#5A6B73] text-lg md:text-xl leading-relaxed max-w-3xl">
            Mundalo connects vetted professionals with members who are actively
            planning their relocation. List your services free while we are in
            early access.
          </p>

          {/* Value cards */}
          <div className="grid md:grid-cols-3 gap-5 mt-12">
            <ValueCard
              testId="bap-card-warm-leads"
              eyebrow="Warm Leads"
              body="Every member who contacts you has already been through our onboarding. They are serious about moving. Not browsing."
            />
            <ValueCard
              testId="bap-card-managed-intros"
              eyebrow="Managed Introductions"
              body="All communications flow through Mundalo. You receive structured inquiries with context. No spam. No cold outreach."
            />
            <ValueCard
              testId="bap-card-free-access"
              eyebrow="Free Early Access"
              body="Listing is free while we are in early access. Be among the first vetted professionals in the Mundalo network."
            />
          </div>

          <p className="mt-10 font-body italic text-[#5A6B73] text-[15px]">
            We review every application personally. You will hear from us within 3 to 5 business days.
          </p>
        </section>

        {/* FORM or SUCCESS */}
        <section className="max-w-3xl mx-auto px-6 md:px-10 pb-24">
          {submitted ? (
            <div
              data-testid="bap-success"
              className="bg-white border border-[#0A1F2E]/12 rounded-2xl p-10 md:p-12"
            >
              <p className="font-mono uppercase text-[11px] tracking-[0.22em] text-[#2CA8A0] mb-4">
                Application Received
              </p>
              <h2 className="font-display italic font-medium text-[#0A1F2E] text-4xl md:text-5xl leading-[1.1] mb-5">
                Thank you for applying.
              </h2>
              <p className="font-body text-[#0A1F2E] text-[16px] leading-relaxed">
                We have received your application and will review it personally.
                You will hear from us within 3 to 5 business days at{' '}
                <strong data-testid="bap-success-email">
                  {form.contact_email}
                </strong>
                . If you have questions in the meantime email us at{' '}
                <a
                  href="mailto:hello@mundalo.com"
                  className="text-[#2CA8A0] underline"
                >
                  hello@mundalo.com
                </a>
                .
              </p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-testid="bap-form"
              className="bg-white border border-[#0A1F2E]/10 rounded-2xl p-7 md:p-10"
            >
              <h2 className="font-display italic font-medium text-[#0A1F2E] text-3xl md:text-4xl mb-8">
                Apply to join the partner network
              </h2>

              {/* SECTION 1 */}
              <div className="space-y-5 mb-10">
                <p className="font-mono uppercase text-[10px] tracking-[0.20em] text-[#1A5C6E]">
                  Section 1 · Your Practice
                </p>

                <div>
                  <label className={labelCls}>What type of service do you provide? *</label>
                  <select
                    data-testid="bap-service-type"
                    required
                    value={form.service_type}
                    onChange={(e) => set('service_type', e.target.value)}
                    className={inputCls}
                  >
                    <option value="">- Select a service type -</option>
                    {SERVICE_TYPES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelCls}>Your full name *</label>
                  <input
                    data-testid="bap-applicant-name"
                    type="text"
                    required
                    value={form.applicant_name}
                    onChange={(e) => set('applicant_name', e.target.value)}
                    className={inputCls}
                  />
                </div>

                <div>
                  <label className={labelCls}>Your business or practice name *</label>
                  <input
                    data-testid="bap-business-name"
                    type="text"
                    required
                    value={form.business_name}
                    onChange={(e) => set('business_name', e.target.value)}
                    className={inputCls}
                  />
                </div>

                <div>
                  <label className={labelCls}>Your primary city or cities of operation in Mexico *</label>
                  <input
                    data-testid="bap-cities"
                    type="text"
                    required
                    placeholder="e.g. Mexico City, Merida, Puerto Vallarta"
                    value={form.cities}
                    onChange={(e) => set('cities', e.target.value)}
                    className={inputCls}
                  />
                </div>

                <div>
                  <label className={labelCls}>Languages spoken *</label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {LANGUAGES.map((lang) => (
                      <label
                        key={lang}
                        className={`flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg border transition-colors ${
                          form.languages.includes(lang)
                            ? 'border-[#2CA8A0] bg-[#2CA8A0]/8 text-[#0A1F2E]'
                            : 'border-[#d5d0c8] text-[#5A6B73] hover:border-[#2CA8A0]/40'
                        }`}
                      >
                        <input
                          type="checkbox"
                          data-testid={`bap-lang-${lang.toLowerCase()}`}
                          checked={form.languages.includes(lang)}
                          onChange={() => toggleLanguage(lang)}
                          className="accent-[#2CA8A0]"
                        />
                        <span className="text-sm">{lang}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* SECTION 2 */}
              <div className="space-y-5 mb-10">
                <p className="font-mono uppercase text-[10px] tracking-[0.20em] text-[#1A5C6E]">
                  Section 2 · Your Contact Information
                </p>

                <div>
                  <label className={labelCls}>Your professional email address *</label>
                  <input
                    data-testid="bap-contact-email"
                    type="email"
                    required
                    value={form.contact_email}
                    onChange={(e) => set('contact_email', e.target.value)}
                    className={inputCls}
                  />
                  <p className="mt-1.5 text-xs text-[#5A6B73]">
                    This email will never be shown to members. It is used only to send
                    you member inquiry notifications.
                  </p>
                  {emailTrim && !emailOk && (
                    <p
                      data-testid="bap-email-invalid-hint"
                      className="mt-1.5 text-xs text-[#C0392B]"
                    >
                      Please enter a complete email address (e.g. you@yourdomain.com).
                    </p>
                  )}
                </div>

                <div>
                  <label className={labelCls}>Your phone number including country code *</label>
                  <input
                    data-testid="bap-phone"
                    type="tel"
                    required
                    placeholder="+52 for Mexico numbers"
                    value={form.phone}
                    onChange={(e) => set('phone', e.target.value)}
                    className={inputCls}
                  />
                </div>

                <div>
                  <label className={labelCls}>Your website or LinkedIn profile</label>
                  <input
                    data-testid="bap-website"
                    type="url"
                    value={form.website}
                    onChange={(e) => set('website', e.target.value)}
                    className={inputCls}
                  />
                </div>
              </div>

              {/* SECTION 3 */}
              <div className="space-y-5 mb-10">
                <p className="font-mono uppercase text-[10px] tracking-[0.20em] text-[#1A5C6E]">
                  Section 3 · Tell Us About Your Practice
                </p>

                <div>
                  <label className={labelCls}>
                    Describe your service and your experience working with expats or foreign nationals in Mexico *
                  </label>
                  <textarea
                    data-testid="bap-description"
                    required
                    rows={5}
                    placeholder="Tell us about your background, how long you have been practicing, your experience with American clients, and what makes your service valuable to someone relocating to Mexico."
                    value={form.description}
                    onChange={(e) => set('description', e.target.value)}
                    className={inputCls}
                  />
                  <p
                    data-testid="bap-description-counter"
                    className={`mt-1.5 text-xs ${descOk ? 'text-[#1A7F4F]' : 'text-[#5A6B73]'}`}
                  >
                    {descCount} / 100 minimum characters
                  </p>
                </div>

                <div>
                  <label className={labelCls}>What is your typical fee structure? *</label>
                  <textarea
                    data-testid="bap-fee-structure"
                    required
                    rows={3}
                    placeholder="e.g. Fixed fee for visa consultations, hourly rate for legal services, commission-based for real estate. Members appreciate transparency on pricing."
                    value={form.fee_structure}
                    onChange={(e) => set('fee_structure', e.target.value)}
                    className={inputCls}
                  />
                </div>

                <div>
                  <label className={labelCls}>
                    How many American or expat clients have you worked with? *
                  </label>
                  <select
                    data-testid="bap-expat-count"
                    required
                    value={form.expat_clients_count}
                    onChange={(e) => set('expat_clients_count', e.target.value)}
                    className={inputCls}
                  >
                    <option value="">- Select an option -</option>
                    {EXPAT_OPTIONS.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* SECTION 4 */}
              <div className="space-y-5 mb-10">
                <p className="font-mono uppercase text-[10px] tracking-[0.20em] text-[#1A5C6E]">
                  Section 4 · Verification Documents
                </p>

                <div>
                  <label className={labelCls}>Upload your professional credentials *</label>
                  <input
                    data-testid="bap-credential-files"
                    type="file"
                    accept={ACCEPT}
                    multiple
                    required
                    onChange={onCredentialFiles}
                    className="block w-full text-sm text-[#0A1F2E] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-mono file:uppercase file:tracking-wider file:bg-[#0A1F2E] file:text-white hover:file:bg-[#0A1F2E]/90"
                  />
                  <p className="mt-1.5 text-xs text-[#5A6B73]">
                    Upload documents that verify your professional credentials. Examples:
                    bar license, medical license, professional certifications, business
                    registration, or any government-issued credential relevant to your
                    field. PDF, JPG, PNG · max 10 MB per file · max 3 files.
                  </p>
                  {credentialFiles.length > 0 && (
                    <ul className="mt-2 text-xs text-[#1A7F4F] space-y-0.5">
                      {credentialFiles.map((f, i) => (
                        <li key={i}>✓ {f.name}</li>
                      ))}
                    </ul>
                  )}
                </div>

                <div>
                  <label className={labelCls}>Upload a government-issued photo ID *</label>
                  <input
                    data-testid="bap-id-file"
                    type="file"
                    accept={ACCEPT}
                    required
                    onChange={onIdFile}
                    className="block w-full text-sm text-[#0A1F2E] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-mono file:uppercase file:tracking-wider file:bg-[#0A1F2E] file:text-white hover:file:bg-[#0A1F2E]/90"
                  />
                  <p className="mt-1.5 text-xs text-[#5A6B73]">
                    A copy of your passport, Mexican national ID, or professional
                    license with photo. Used for identity verification only. Never
                    shared with members. PDF, JPG, PNG · max 10 MB.
                  </p>
                  {idFile && (
                    <p className="mt-2 text-xs text-[#1A7F4F]">✓ {idFile.name}</p>
                  )}
                </div>

                <div>
                  <label className={labelCls}>Upload any additional supporting documents</label>
                  <input
                    data-testid="bap-supporting-files"
                    type="file"
                    accept={ACCEPT}
                    multiple
                    onChange={onSupportingFiles}
                    className="block w-full text-sm text-[#0A1F2E] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-mono file:uppercase file:tracking-wider file:bg-[#0A1F2E] file:text-white hover:file:bg-[#0A1F2E]/90"
                  />
                  <p className="mt-1.5 text-xs text-[#5A6B73]">
                    Optional. Client testimonials, portfolio samples, certifications,
                    or anything else that helps us understand your practice. PDF, JPG,
                    PNG · max 10 MB per file · max 5 files.
                  </p>
                  {supportingFiles.length > 0 && (
                    <ul className="mt-2 text-xs text-[#1A7F4F] space-y-0.5">
                      {supportingFiles.map((f, i) => (
                        <li key={i}>✓ {f.name}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* SECTION 5 */}
              <div className="space-y-4 mb-10">
                <p className="font-mono uppercase text-[10px] tracking-[0.20em] text-[#1A5C6E]">
                  Section 5 · Agreement
                </p>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="bap-agreement-accuracy"
                    checked={form.agreement_accuracy}
                    onChange={(e) => set('agreement_accuracy', e.target.checked)}
                    required
                    className="mt-1 accent-[#2CA8A0]"
                  />
                  <span className="text-sm text-[#0A1F2E] leading-relaxed">
                    I confirm that all information provided is accurate and that I am
                    authorized to offer the services described. I understand that
                    Mundalo will review my application and contact me within 3 to 5
                    business days. *
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="bap-agreement-platform"
                    checked={form.agreement_platform}
                    onChange={(e) => set('agreement_platform', e.target.checked)}
                    required
                    className="mt-1 accent-[#2CA8A0]"
                  />
                  <span className="text-sm text-[#0A1F2E] leading-relaxed">
                    I agree that if approved, all member communications will be managed
                    through the Mundalo platform and I will not solicit direct contact
                    information from members outside of Mundalo&apos;s messaging system. *
                  </span>
                </label>
              </div>

              {submitError && (
                <p
                  data-testid="bap-submit-error"
                  className="mb-4 text-sm text-[#C0392B] bg-[#C0392B]/8 border border-[#C0392B]/20 rounded-lg p-3"
                >
                  {submitError}
                </p>
              )}

              <div className="flex justify-center">
                <button
                  type="submit"
                  data-testid="bap-submit-btn"
                  disabled={!canSubmit}
                  className={`w-full md:w-auto inline-block font-mono uppercase text-[12px] tracking-[0.18em] font-semibold px-10 py-4 rounded-full transition-all ${
                    canSubmit
                      ? 'bg-[#E8A830] text-[#0A1F2E] hover:brightness-95 hover:-translate-y-px shadow-sm'
                      : 'bg-[#d5d0c8] text-[#5A6B73] cursor-not-allowed'
                  }`}
                >
                  {submitting ? 'Submitting…' : 'Submit My Application'}
                </button>
              </div>
            </form>
          )}
        </section>
      </main>

      <Footer />
    </>
  );
}
